import React from 'react';
import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { Transaction } from '../../types/Transaction';
import { getUiSignedAmount } from '../../utils/transactionAmount';
import { useFormatting } from '../../context/FormattingContext';
import { formatMoney } from '../../utils/formatting';

type Props = {
  open: boolean;
  transaction: Transaction | null;
  onClose: () => void;
};

type TxEntry = {
  accountId?: string;
  accountName?: string;
  amountSigned?: number;
  displayAmountSigned?: number;
};

const getKindLabel = (tx: Transaction): string => {
  const kindOrType = tx.kind || tx.type;
  if (kindOrType === 'income') return 'Income';
  if (kindOrType === 'expense') return 'Expense';
  if (kindOrType === 'transfer') return 'Transfer';
  if (kindOrType === 'cardPurchase') return 'Card Purchase';
  if (kindOrType === 'cardPayment') return 'Card Payment';
  if (kindOrType === 'adjustment') return 'Adjustment';
  if (kindOrType === 'savings') return 'Savings';
  return kindOrType || '—';
};

const formatSignedAmount = (value: number | undefined, locale: string, currency: string): string => {
  if (typeof value !== 'number') return '—';
  const sign = value >= 0 ? '+' : '−';
  const absolute = formatMoney(Math.abs(value), { locale, currency });
  if (absolute === '—') return '—';
  return `${sign}${absolute}`;
};

const getSummaryAmount = (tx: Transaction, locale: string, currency: string): string => {
  const uiSigned = getUiSignedAmount(tx);
  if (typeof uiSigned === 'number') return formatSignedAmount(uiSigned, locale, currency);
  return formatMoney(Math.abs(tx.amount || 0), { locale, currency });
};

const getMovementLabel = (tx: Transaction): string => {
  const direction = tx.direction || {
    fromAccountName: tx.fromAccountName,
    toAccountName: tx.toAccountName,
  };
  if (direction.fromAccountName && direction.toAccountName) {
    return `${direction.fromAccountName} → ${direction.toAccountName}`;
  }
  return '—';
};

const getExplainabilityBullets = (tx: Transaction): string[] => {
  switch (tx.kind) {
    case 'income':
      return ['Adds money to an asset account (for example, checking), so the signed amount is positive.'];
    case 'expense':
      return ['Removes money from an asset account, so the signed amount is negative.'];
    case 'transfer':
      return ['Moves money between two asset accounts. One entry is negative (source), one is positive (destination). Net worth typically stays the same.'];
    case 'cardPurchase':
      return [
        'Shown as an expense outflow in this UI, so the displayed amount is negative.',
        'Ledger entries for this transaction increase the credit card liability (what you owe).',
        'The entries list remains the source of truth for accounting signs and account-level effects.',
      ];
    case 'cardPayment':
      return ['Reduces cash (asset decreases) and reduces credit card liability. Both entries may be negative depending on your ledger convention.'];
    case 'adjustment':
      return ['Manual correction to align balances; sign depends on whether the adjustment increases or decreases the account.'];
    default:
      return [];
  }
};

const TransactionDetailsDrawer: React.FC<Props> = ({ open, transaction, onClose }) => {
  const { locale, currency } = useFormatting();
  const entries: TxEntry[] = Array.isArray((transaction as Transaction & { entries?: TxEntry[] } | null)?.entries)
    ? ((transaction as Transaction & { entries?: TxEntry[] }).entries as TxEntry[])
    : [];

  const accountImpactMap = entries.reduce((acc, entry) => {
    const signed =
      typeof entry.displayAmountSigned === 'number'
        ? entry.displayAmountSigned
        : typeof entry.amountSigned === 'number'
          ? entry.amountSigned
          : undefined;
    if (typeof signed !== 'number') return acc;
    const key = entry.accountName || entry.accountId;
    if (!key) return acc;
    acc.set(key, (acc.get(key) || 0) + signed);
    return acc;
  }, new Map<string, number>());

  const accountImpactLine = Array.from(accountImpactMap.entries())
    .map(([name, delta]) => `${name}: ${formatSignedAmount(delta, locale, currency)}`)
    .join(', ');

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (t) => t.zIndex.appBar + 1 }}
      PaperProps={{
        'data-testid': 'transaction-details-drawer-paper',
        style: {
          top: 'var(--app-topbar-height, 64px)',
          height: 'calc(100% - var(--app-topbar-height, 64px))',
        },
        sx: {
          top: 'var(--app-topbar-height, 64px)',
          height: 'calc(100% - var(--app-topbar-height, 64px))',
        },
      }}
    >
      <Box sx={{ width: { xs: '100vw', sm: 520 }, p: 2.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6">Transaction Details</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {transaction ? `${formatDateForDisplay(transaction.date)} • ${getKindLabel(transaction)}` : 'No transaction selected'}
            </Typography>
          </Box>
          <IconButton aria-label="close details" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {!transaction ? (
          <Typography sx={{ color: 'text.secondary', mt: 2 }}>
            No transaction selected.
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Summary</Typography>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText primary="Kind" secondary={getKindLabel(transaction)} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="Category" secondary={transaction.category || '—'} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="Amount" secondary={getSummaryAmount(transaction, locale, currency)} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="Description" secondary={transaction.description || '—'} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="Tentative" secondary={transaction.tentative ? 'Yes' : 'No'} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="Source" secondary={transaction.source || '—'} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText
                    primary="Recurring"
                    secondary={
                      transaction.isRecurring
                        ? `Yes • ${transaction.frequency || 'No frequency'} • Next: ${transaction.nextDueDate ? formatDateForDisplay(transaction.nextDueDate) : '—'} • End: ${transaction.endDate ? formatDateForDisplay(transaction.endDate) : '—'}`
                        : 'No'
                    }
                  />
                </ListItem>
              </List>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                {(transaction.tags || []).length > 0 ? (
                  (transaction.tags || []).map((tag) => <Chip key={tag} size="small" label={tag} />)
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>No tags</Typography>
                )}
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Movement</Typography>
              <Typography data-testid="transaction-details-movement">
                {getMovementLabel(transaction)}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Entries</Typography>
              {entries.length > 0 ? (
                <TableContainer
                  data-testid="transaction-details-entries-scroll"
                  sx={{ overflowX: 'auto' }}
                >
                  <Table size="small" sx={{ minWidth: 320 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Account</TableCell>
                        <TableCell>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entries.map((entry, idx) => {
                        const signed =
                          typeof entry.displayAmountSigned === 'number'
                            ? entry.displayAmountSigned
                            : entry.amountSigned;
                        return (
                          <TableRow key={`${entry.accountId || entry.accountName || 'entry'}-${idx}`}>
                            <TableCell>{entry.accountName || entry.accountId || '—'}</TableCell>
                            <TableCell>{formatSignedAmount(signed, locale, currency)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography sx={{ color: 'text.secondary' }}>
                  No entries available for this transaction.
                </Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Why this sign?</Typography>
              <List dense disablePadding>
                {getExplainabilityBullets(transaction).map((bullet) => (
                  <ListItem key={bullet} disableGutters>
                    <ListItemText primary={`• ${bullet}`} />
                  </ListItem>
                ))}
              </List>
              {accountImpactLine && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                  Account impacts: {accountImpactLine}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                What accounts changed? {entries.length > 0 ? 'See entries and account impacts above.' : 'Entry-level account changes are unavailable for this transaction.'}
              </Typography>
            </Box>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

export default TransactionDetailsDrawer;
